/**
 * First-touch attribution capture. Must run BEFORE keycloak-js initializes:
 * keycloak.ts redirectToCleanUrl() and public/pkce-recovery.js both rewrite
 * the URL to origin+pathname, destroying the query string.
 *
 * Stored in localStorage (not sessionStorage) with a 30-day TTL so it survives
 * the email-verification link opening a new tab, browser restarts mid-signup,
 * and in-app-browser hand-offs.
 */

export const ATTRIBUTION_STORAGE_KEY = 'rosie:attribution';

const ALLOWED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'ttclid',
  'li_fat_id',
  'twclid',
  'msclkid',
  'rdt_cid',
] as const;

const MAX_LEN = 200;
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — industry norm for first-touch
// Date's representable range is ±8.64e15 epoch-ms; new Date(1e16).toISOString()
// THROWS. Values past this bound must be rejected at capture, or a single
// crafted ?att_ts= link becomes a persistent blank-screen crash at report time.
const MAX_TS = 8.64e15;

export interface StoredAttribution {
  params: Record<string, string>;
  referrer?: string;
  landingPage?: string;
  landedAt?: number;
  exp?: number;
}

// Hoisted to module scope so the disable comment stays attached to the pattern
// no matter how Prettier reflows the call chain below. Safe to share: String
// .replace() with a /g regex resets lastIndex on every call.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x1f\x7f]/g;

function sanitize(value: string, max = MAX_LEN): string {
  return value.replace(CONTROL_CHARS, '').trim().slice(0, max);
}

// The marketing site omits att_ref for same-site referrers; this guard keeps
// the document.referrer FALLBACK from reintroducing androcrew.com as the
// "acquisition referrer" one layer down (QR/offline/direct-typed campaigns).
function externalReferrer(ref: string): string {
  if (!ref) return '';
  try {
    const host = new URL(ref).hostname;
    if (host === 'androcrew.com' || host.endsWith('.androcrew.com')) return '';
  } catch {
    return '';
  }
  return sanitize(ref, 512);
}

export function captureAttributionParams(
  search: string = window.location.search,
  referrer: string = document.referrer
): void {
  try {
    if (getStoredAttribution()) return; // first touch wins (getStored prunes expired)
    const query = new URLSearchParams(search);
    const params: Record<string, string> = {};
    for (const name of ALLOWED_PARAMS) {
      const value = query.get(name);
      if (value) {
        const clean = sanitize(value);
        if (clean) params[name] = clean;
      }
    }
    const attRef = query.get('att_ref');
    const attPage = query.get('att_page');
    const attTs = Number(query.get('att_ts'));
    if (Object.keys(params).length === 0 && !attRef && !attPage) return;
    const stored: StoredAttribution = { params, exp: Date.now() + TTL_MS };
    const ref = attRef ? sanitize(attRef, 512) : externalReferrer(referrer);
    if (ref) stored.referrer = ref;
    if (attPage) stored.landingPage = sanitize(attPage, 512);
    if (Number.isFinite(attTs) && attTs > 0 && attTs <= MAX_TS) stored.landedAt = attTs;
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // storage unavailable (private mode edge cases) — attribution is best-effort
  }
}

export function getStoredAttribution(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    // Parsed as unknown, then narrowed: casting straight to StoredAttribution
    // would make the shape check below look statically dead to eslint while
    // JSON.parse can in fact return anything.
    const parsed: unknown = JSON.parse(raw);
    // Shape check: a malformed blob (hand-edited storage, schema drift) would
    // otherwise 422 on every session until its TTL — drop it instead.
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { params?: unknown }).params !== 'object' ||
      (parsed as { params?: unknown }).params === null
    ) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    const stored = parsed as StoredAttribution;
    if (stored.exp && stored.exp < Date.now()) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

export function clearStoredAttribution(): void {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // ignore
  }
}
